//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { Fragment, useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import MessageIcon from '@material-ui/icons/Message';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import BusinessIcon from '@material-ui/icons/Business';
import SchoolIcon from '@material-ui/icons/School';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { makeStyles, Badge, IconButton, Menu, MenuItem, Tooltip } from '@material-ui/core';
import PageviewOutlinedIcon from '@material-ui/icons/PageviewOutlined';
import { mdiSchoolOutline } from '@mdi/js';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { logEvent } from '../../system/firebase';
import { AppContext, LayoutContext } from '../../common/contexts';
import { copyTextToClipboard } from '../../system/helpers/systemHelper';
import { setClassNameInDefns } from '../../common/definitionHelper';
import {
    DASHBOARD_MY_MESSAGES,
    CONFIRM_DIALOG_TITLE_MAKE_JOB_INACTIVE,
    CONFIRM_DIALOG_TITLE_MAKE_JOB_ACTIVE,
    CONFIRM_DIALOG_TITLE_EMP_DELETE_JOB,
    CONFIRM_DIALOG_MESSAGE_EMP_DELETE_JOB,
    CONFIRM_DIALOG_MESSAGE_MAKE_JOB_INACTIVE,
    CONFIRM_DIALOG_MESSAGE_MAKE_JOB_ACTIVE,
    ANALYTICS_EVENT_JOB_LISTING_SET_ACTIVE,
    ANALYTICS_EVENT_JOB_LISTING_SET_INACTIVE,
    ANALYTICS_EVENT_JOB_LISTING_DELETED,
} from '../../common/constants';
import { BASE_URL, DASHBOARD_ID_MY_SETTINGS, DASHBOARD_ID_MY_SETTINGS_SUBSCRIPTION } from '../../common/constants';
import { PATH_DASHBOARD } from '../../system/constants';
import { getSalaryRateText, getCityStateCountryText, nl2br, getJobYearsOfExperienceText } from '../../common/textHelper';
import { getUniqueKey } from '../../common/arrayHelper';
import { getEmpMyJobShareUrl } from '../../common/linkHelper';
import { getUserDateFormat, getFormattedDate } from '../../common/dateHelper';
import { getEmployerDataForActiveJob } from '../../common/employerHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { updateJob, deleteJob } from '../../common/services/empMyJobsServices';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdMdiIcon from '../../common/elemental/AfitdMdiIcon';
import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdConfirmDialog from '../../common/elemental/AfitdConfirmDialog';
// import AfitdCompanyName from '../../common/elemental/AfitdCompanyName';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
import cx from 'clsx';
import _get from 'lodash/get';
import _map from 'lodash/map';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    return makeStyles(theme => ({
        headingSection: {
            borderTop: '1px solid rgb(240,242,246)',
            paddingTop: 24,
            marginTop: 24,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            [theme.breakpoints.down('lg')]: { flexDirection: 'column-reverse', alignItems: 'flex-start' },
        },
        heading: {
            fontWeight: 'bold',
            marginRight: '10px',
            [theme.breakpoints.down('xl')]: {
                maxWidth: '580px',
            },
            [theme.breakpoints.down('lg')]: {
                maxWidth: '560px',
            },
            [theme.breakpoints.down('md')]: {
                maxWidth: '520px',
            },
            [theme.breakpoints.down('sm')]: {
                maxWidth: '265px',
                marginRight: '0px',
            },
        },
        titleIcon: {
            padding: 0,
            marginLeft: 32, //20,
            [theme.breakpoints.down('lg')]: {
                marginLeft: 12, //0,
            },
            '&.MuiIconButton-root:hover': { background: 'rgba(0, 0, 0, 0)' },
        },
        headingLeftContainer: { cursor: 'pointer', display: 'flex', alignItems: 'flex-start', maxWidth: '60%', marginBottom: 6 },
        jobTitleReqNumContainer: {},
        reqNumContainer: { display: 'flex', alignItems: 'center' },
        reqNumText: { fontStyle: 'italic' },
        singleJobIconButton: {},
        statusText: { color: '#1D8A8B', textTransform: 'uppercase', fontWeight: 'bold', marginRight: 15 },
        statusDateLabel: { color: '#1D8A8B', textTransform: 'uppercase', fontWeight: 'bold', fontStyle: 'italic', marginRight: 8 },
        statusDateText: { color: '#1D8A8B', textTransform: 'uppercase', fontWeight: 'bold', marginRight: 15 },
        subInfo: { color: '#1D8A8B', textTransform: 'uppercase', fontWeight: 'bold', marginRight: 15 },
        verifyIcon: { color: '#1D8A8B', margin: '0 5px' },
        jobLocationText: { fontWeight: 'normal', marginBottom: 10 },
        detailContainer: { flexDirection: 'row', display: 'flex', alignItems: 'center', marginRight: 20 },
        address: { fontWeight: 'bold' },
        detail: { fontWeight: 'bold' },
        icons: { color: '#9B9E9C', fontSize: 20, marginRight: 10 },
        messageIcon: { color: '#9B9E9C', fontSize: 20 },
        subInfoContainer: {
            display: 'flex',
            alignItems: 'flex-start', //'center',
            justifyContent: 'center',
            [theme.breakpoints.down('lg')]: { width: '100%', justifyContent: 'flex-end', alignItems: 'center' },
            [theme.breakpoints.down('sm')]: { justifyContent: 'space-between', alignItems: 'center' },
        },
        miscInfoContainer: { display: 'flex', justifyContent: 'space-between' },
        miscInfo: { display: 'flex', flex: 1, flexWrap: 'wrap' },
        comingSoonContainer: { margin: '15px 125px 0 0', wordBreak: 'break-word' },
        filledContainer: { margin: '15px 125px 0 0', wordBreak: 'break-word' },
        descriptionTextContainer: { margin: '15px 125px 0 0', wordBreak: 'break-word' },
        savedListText: { color: 'rgb(29,138,139)', fontWeight: 'bold', marginRight: '10px' },
        appliedListText: { color: 'rgb(252,130,31)', fontWeight: 'bold', marginRight: '10px' },
        settingsIcon: { cursor: 'pointer' },
        savedList: { backgroundColor: 'rgba(29,138,139,0.09)' },
        appliedList: { backgroundColor: '#FFF8E5' },
        list: {
            borderRadius: '4px',
            marginTop: '16px',
            padding: '15px',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
        },
        badge: { cursor: 'pointer' },
        inactiveStatus: { color: 'red' },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        props,
        classes,
        userDateFormat,
        // tiers,
        // currentEmployer,
        handleShowListDialog,
        setAnchorEl,
        anchorEl,
        active,
        handleDialog,
        confirmDeleteDialogOpen,
        setConfirmDeleteDialogOpen,
        handleMenuAction,
        value,
        limitModal,
        setLimitModal,
        jobId,
        unreadNewApplicantsAll,
        handleClickShareJobUrlMenu,
        handleConfirmActiveJobLimitDialog,
    } = params;
    const { job, idx, onJobTitleClick } = props;
    const { savedList: savedListCls, appliedList: appliedListCls, list: listCls, inactiveStatus: inactiveStatusCls } = classes;

    const reqNum = _get(job, 'reqNum');
    const updateDt = _get(job, 'updateDt');
    const statusDt = _get(job, 'statusDt');
    const city = _get(job, 'city');
    const state = _get(job, 'state');
    const country = _get(job, 'country');

    const reqNumText = reqNum ? `ID#: ${reqNum}` : null;

    const jobLocationText = getCityStateCountryText(city, state, country);

    const unreadNewApplicants = _get(unreadNewApplicantsAll, jobId, []);
    const unreadNewApplicantsLength = unreadNewApplicants.length;
    const isHideUnreadNewApplicantsBadge = unreadNewApplicantsLength === 0;

    const dateInfoLabel = active ? 'Updated on' : 'Since';
    const dateInfoDate = active ? updateDt : statusDt;

    const defns = {
        jobTitleBadge: {
            variant: 'dot',
            color: 'primary',
            invisible: isHideUnreadNewApplicantsBadge,
        },
        headingLeftContainer: { onClick: () => onJobTitleClick(idx) },
        heading: { variant: 'h6' },
        statusText: { variant: 'caption', color: 'secondary', classNameMixin: cx({ [inactiveStatusCls]: !active }), value: active ? 'Active' : 'Inactive' },
        dateInfo: { label: dateInfoLabel, date: dateInfoDate, dateFormat: userDateFormat, active },
        subInfo: { variant: 'caption' },
        address: { variant: 'body1' },
        detail: { variant: 'body1' },
        badge: { color: 'secondary', overlap: 'circle', variant: 'dot' },
        reqNumText: { variant: 'body2', value: reqNumText },
        internshipIcon: {
            // color: 'watermark',
            style: { color: '#9B9E9C', width: 18, marginRight: 10 },
            path: mdiSchoolOutline,
        },
        jobLocationText: { variant: 'body1', value: jobLocationText },
        descriptionText: { variant: 'body2' },
        listText: { variant: 'body2' },
        comingSoonText: { variant: 'body1', color: 'secondary', clsSkinnyBold: true },
        filledText: { variant: 'body1', color: 'secondary', clsSkinnyBold: true },
        savedListText: { variant: 'body2' },
        savedList: { onClick: () => handleShowListDialog(true), className: cx(listCls, savedListCls) },

        applicantsBadge: {
            color: 'primary',
            badgeContent: unreadNewApplicantsLength,
            invisible: isHideUnreadNewApplicantsBadge,
        },
        appliedListText: { variant: 'body2' },
        appliedList: { onClick: () => handleShowListDialog(false), className: cx(listCls, appliedListCls) },
        menu: { id: 'long-menu', anchorEl: anchorEl, keepMounted: true, open: Boolean(anchorEl), onClose: () => setAnchorEl(null) },
        moreButton: { 'aria-controls': 'long-menu', 'aria-haspopup': true, onClick: evt => setAnchorEl(evt.currentTarget) },
        activeOptionMenu: { onClick: () => handleDialog('toggleActive') },
        editOptionMenu: { onClick: () => handleDialog('edit') },
        deleteOptionMenu: { onClick: () => handleDialog('delete') },
        shareJobUrlMenu: { onClick: handleClickShareJobUrlMenu },
        hiredOptionMenu: { onClick: () => handleDialog('hired') },
        confirmDeleteDialog: {
            open: confirmDeleteDialogOpen,
            onClose: () => setConfirmDeleteDialogOpen(false),
            onConfirm: () => handleMenuAction(),
            title: value === 'delete' ? CONFIRM_DIALOG_TITLE_EMP_DELETE_JOB : active ? CONFIRM_DIALOG_TITLE_MAKE_JOB_INACTIVE : CONFIRM_DIALOG_TITLE_MAKE_JOB_ACTIVE,
            message: value === 'delete' ? CONFIRM_DIALOG_MESSAGE_EMP_DELETE_JOB : active ? CONFIRM_DIALOG_MESSAGE_MAKE_JOB_INACTIVE : CONFIRM_DIALOG_MESSAGE_MAKE_JOB_ACTIVE,
            yesButtonColor: 'secondary',
        },
        limitDialog: {
            open: limitModal,
            onClose: () => setLimitModal(false),
            onConfirm: handleConfirmActiveJobLimitDialog,
            message: `Your limit of simultaneous 'Active' jobs has been reached. Please upgrade your subscription to post more job listings.`,
            yesButtonLabel: 'Upgrade',
            yesButtonColor: 'secondary',
            noButtonLabel: 'Cancel',
            title: 'Interaction Limit Exceeded!',
        },

        titleIcon: { title: 'View Job Details' },
    };

    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const DateInfo = props => {
    const classes = useStyles()();
    const { label, date, dateFormat } = props;
    const dateText = getFormattedDate(date, dateFormat);
    const defns = {
        statusDateLabel: { variant: 'caption', component: 'span', value: `${label}:` },
        statusDateText: { variant: 'caption', component: 'span', value: dateText },
    };
    setClassNameInDefns(defns, classes);
    return (
        <Fragment>
            <AfitdTypography {...defns.statusDateLabel} />
            <AfitdTypography {...defns.statusDateText} />
        </Fragment>
    );
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const JobCard = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles(/*{param1, param2}*/)();
    const appContext = useContext(AppContext);
    const layoutContext = useContext(LayoutContext);
    const { userId, currentEmployer } = appContext;
    const tiers = _get(appContext, 'sys.tiers');
    const { setSnackbarMessage, setSnackbarErrorMessage, dashboardData, setAsyncCallCount, profileData, setIsSavedList, setSelectedJobForListModal } = layoutContext;
    const history = useHistory();
    const unreadNewApplicantsAll = _get(dashboardData, 'unreadNewApplicants');
    const userDateFormat = getUserDateFormat(appContext);

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [limitModal, setLimitModal] = useState(false);
    const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [value, setValue] = React.useState();

    //---------------------------------------------------------------------
    // from props
    //---------------------------------------------------------------------
    const { job, limitExceeded, onJobDelete, onJobStatusChange, onJobMenuEditClick, onJobMenuHiredCandidatesClick } = props;

    //---------------------------------------------------------------------
    // derived
    //---------------------------------------------------------------------
    const jobId = _get(job, 'uid');
    const {
        intern,
        jobDescription,
        education,
        isExactRate,
        rateMin,
        rateMax,
        jobTitle,
        yrsOfExp,
        // createDt,
        currency,
        currencySymbol,
        employmentType,
        telework,
        active,
        jobSeekers,
        conversations,
        applicants,
        savedJs,
        payFrequency,
        otherEducation,
        comingSoon,
        filled,
        // rate,
    } = job;
    const conversationExists = (conversations || []).find(el => el.members.includes(userId));
    const yrsOfExpText = getJobYearsOfExperienceText(yrsOfExp);

    let appliedJobSeekers = [];
    let savedJobSeekers = [];
    if (job) {
        appliedJobSeekers = _map(applicants, jobSeekerId => ({ ..._get(jobSeekers, jobSeekerId), userId: jobSeekerId }));
        savedJobSeekers = _map(savedJs, jobSeekerId => ({ ..._get(jobSeekers, jobSeekerId), userId: jobSeekerId }));
    }

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Event handlers
    //---------------------------------------------------------------------
    const handleShowListDialog = val => {
        setIsSavedList(val);
        setSelectedJobForListModal(job);
    };

    const handleDialog = val => {
        setValue(val);
        setAnchorEl(null);
        if (val === 'hired') onJobMenuHiredCandidatesClick && onJobMenuHiredCandidatesClick(job);
        if (val === 'edit') onJobMenuEditClick && onJobMenuEditClick(job);
        setConfirmDeleteDialogOpen(true);
    };

    const handleClickShareJobUrlMenu = async () => {
        setAnchorEl(null);
        const shareUrl = getEmpMyJobShareUrl({ jobId: job?.uid });
        await copyTextToClipboard(shareUrl);
        setSnackbarMessage('Job URL successfully copied to clipboard');
    };

    const handleMenuAction = async () => {
        const jobId = job.uid;
        const newIsActive = !active;
        setConfirmDeleteDialogOpen(false);
        switch (value) {
            case 'toggleActive':
                // if changing to active (newStatus is true) and limit exceeded, disallow action
                if (newIsActive && limitExceeded) {
                    setLimitModal(true);
                } else {
                    const employerData = getEmployerDataForActiveJob({ employer: currentEmployer, profile: profileData });

                    const updatedJob = await updateJob({
                        data: { ...job, ...employerData, active: newIsActive, comingSoon: false, filled: false },
                        activeOrig: active,
                        setSnackbarMessage,
                        setSnackbarErrorMessage,
                    });

                    !!updatedJob && onJobStatusChange && onJobStatusChange(updatedJob, newIsActive);

                    if (!!updatedJob) {
                        if (newIsActive) logEvent(ANALYTICS_EVENT_JOB_LISTING_SET_ACTIVE);
                        else logEvent(ANALYTICS_EVENT_JOB_LISTING_SET_INACTIVE);
                    }
                }
                return;
            case 'delete':
                setAsyncCallCount && setAsyncCallCount(prev => prev + 1);

                const success = await deleteJob(jobId, setSnackbarMessage, setSnackbarErrorMessage);
                success && onJobDelete && onJobDelete(jobId, newIsActive);
                success && logEvent(ANALYTICS_EVENT_JOB_LISTING_DELETED);

                setAsyncCallCount && setTimeout(() => setAsyncCallCount(prev => prev - 1), 500);
                return;
            default:
            // do nothing
        }
    };

    const handleConfirmActiveJobLimitDialog = () => {
        setLimitModal(false);
        history.push(`${BASE_URL}${PATH_DASHBOARD}/${DASHBOARD_ID_MY_SETTINGS}/${DASHBOARD_ID_MY_SETTINGS_SUBSCRIPTION}`);
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({
        props,
        classes,
        userDateFormat,
        tiers,
        currentEmployer,
        handleShowListDialog,
        setAnchorEl,
        anchorEl,
        active,
        handleDialog,
        confirmDeleteDialogOpen,
        setConfirmDeleteDialogOpen,
        handleMenuAction,
        value,
        limitModal,
        setLimitModal,
        jobId,
        unreadNewApplicantsAll,
        handleClickShareJobUrlMenu,
        handleConfirmActiveJobLimitDialog,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <div>
            <div {...defns.headingSection}>
                <div {...defns.headingLeftContainer}>
                    <Badge {...defns.jobTitleBadge}>
                        <div {...defns.jobTitleReqNumContainer}>
                            <AfitdTypography {...defns.heading}>{jobTitle}</AfitdTypography>
                            <div {...defns.reqNumContainer}>
                                <AfitdTypography {...defns.reqNumText} />
                            </div>
                        </div>
                    </Badge>
                    <Tooltip {...defns.titleIcon}>
                        <IconButton disableRipple {...defns.singleJobIconButton}>
                            <PageviewOutlinedIcon />
                        </IconButton>
                    </Tooltip>
                </div>
                <div {...defns.subInfoContainer}>
                    <div>
                        <AfitdTypography {...defns.statusText} />
                        <DateInfo {...defns.dateInfo} />
                    </div>
                    <IconButton {...defns.moreButton}>
                        <MoreVertIcon {...defns.settingsIcon} />
                    </IconButton>
                    <Menu {...defns.menu}>
                        <MenuItem {...defns.editOptionMenu}>Edit</MenuItem>
                        <MenuItem {...defns.activeOptionMenu}>Make {active ? 'Inactive' : 'Active'}</MenuItem>
                        <MenuItem {...defns.shareJobUrlMenu}>Share Job URL</MenuItem>
                        <MenuItem {...defns.hiredOptionMenu}>Candidate Hired ?</MenuItem>
                        <MenuItem {...defns.deleteOptionMenu}>Delete Job</MenuItem>
                    </Menu>
                </div>
            </div>

            <AfitdTypography {...defns.jobLocationText} />
            <div {...defns.miscInfoContainer}>
                <div {...defns.miscInfo}>
                    <div {...defns.detailContainer}>
                        <AfitdTypography {...defns.icons}>{currencySymbol}</AfitdTypography>
                        <AfitdTypography {...defns.detail}>{getSalaryRateText(isExactRate, rateMin, rateMax, currency, payFrequency)}</AfitdTypography>
                    </div>
                    <div {...defns.detailContainer}>
                        <AccessTimeIcon {...defns.icons} />
                        <AfitdTypography {...defns.detail}>{yrsOfExpText}</AfitdTypography>
                    </div>
                    <div {...defns.detailContainer}>
                        <AccessTimeIcon {...defns.icons} />
                        <AfitdTypography {...defns.detail}>{employmentType}</AfitdTypography>
                    </div>
                    <div {...defns.detailContainer}>
                        <BusinessIcon {...defns.icons} />
                        <AfitdTypography {...defns.detail}>{telework}</AfitdTypography>
                    </div>
                    {intern ? (
                        <div {...defns.detailContainer}>
                            <AfitdMdiIcon {...defns.internshipIcon} />
                            <AfitdTypography {...defns.detail}>Intern Position</AfitdTypography>
                        </div>
                    ) : null}
                    <div {...defns.detailContainer}>
                        <SchoolIcon {...defns.icons} />
                        <AfitdTypography {...defns.detail}>{education}</AfitdTypography>
                    </div>
                    {otherEducation?.map(el => (
                        <div {...defns.detailContainer}>
                            <SchoolIcon {...defns.icons} />
                            <AfitdTypography {...defns.detail}>{el}</AfitdTypography>
                        </div>
                    ))}
                </div>
                {conversationExists && (
                    <Badge
                        {...defns.badge}
                        onClick={() =>
                            history.push('/', {
                                pathname: '/',
                                state: { currentDashboard: DASHBOARD_MY_MESSAGES, threadId: conversationExists?.threadId },
                            })
                        }
                    >
                        <MessageIcon {...defns.messageIcon} />
                    </Badge>
                )}
            </div>
            {comingSoon ? (
                <div {...defns.comingSoonContainer}>
                    <AfitdTypography {...defns.comingSoonText}>Coming Soon</AfitdTypography>
                </div>
            ) : null}
            {filled ? (
                <div {...defns.filledContainer}>
                    <AfitdTypography {...defns.filledText}>Already Filled</AfitdTypography>
                </div>
            ) : null}
            <div {...defns.descriptionTextContainer}>
                <AfitdTypography {...defns.descriptionText} key={getUniqueKey()}>
                    {nl2br(jobDescription)}
                </AfitdTypography>
            </div>

            {savedJobSeekers?.length > 0 && (
                <div {...defns.savedList}>
                    <AfitdTypography {...defns.listText}>
                        <strong>{savedJobSeekers?.length} </strong>
                        job seeker{savedJobSeekers?.length > 1 && 's'}
                        <strong> {savedJobSeekers?.length > 1 ? 'have' : 'has'} been saved </strong>
                        to this job
                    </AfitdTypography>
                    <AfitdTypography {...defns.savedListText}>Show list</AfitdTypography>
                </div>
            )}

            {appliedJobSeekers?.length > 0 && (
                <div {...defns.appliedList}>
                    <AfitdTypography {...defns.listText}>
                        <strong>{appliedJobSeekers?.length} </strong>
                        job seeker{appliedJobSeekers?.length > 1 && 's'}
                        <strong> applied </strong>
                        to this job
                    </AfitdTypography>
                    <Badge {...defns.applicantsBadge}>
                        <AfitdTypography {...defns.appliedListText}>Show list</AfitdTypography>
                    </Badge>
                </div>
            )}

            <AfitdConfirmDialog {...defns.confirmDeleteDialog} />
            <AfitdConfirmDialog {...defns.limitDialog} />
        </div>
    );
};

export default JobCard;
